// Variant picker — phone cases (Blanc Space custom)
// Small behaviors, all resilient to the theme's partial rerender
// (event delegation on document; injected elements live in the gallery,
// which is not rerendered on variant change):
//
// 1. Apple/Samsung brand filter inside the phone-model popover

(() => {
	if (window.__vpcInitialized) return;
	window.__vpcInitialized = true;
	const PHONE_MODEL_STORAGE_KEY = 'blancspace.phoneModel';

	const rememberPhoneModel = (model) => {
		if (!model) return;
		if (window.BlancSpaceDevicePreference) {
			window.BlancSpaceDevicePreference.set(model, { source: 'product' });
			return;
		}
		try {
			localStorage.setItem(PHONE_MODEL_STORAGE_KEY, model);
		} catch (error) {
			// Variant selection must still work when storage is unavailable.
		}
		window.dispatchEvent(new CustomEvent('blancspace:phone-model-change', { detail: { model, source: 'inferred' } }));
	};

	/* --- 0. Option changes: closest-match snap, one fetch per tap -------------
	   Our radios use data-vpc-position (not the vanilla data-option-position)
	   so the theme's variant-picker ignores them completely — crucially that
	   disables its "prerender every unchecked option" behavior, which on a
	   43-model product fired ~45 background page renders per picker view and
	   queued real taps behind them. We handle the change ourselves:

	   1. Look the picked combination up in the [data-vpc-matrix] JSON
	      (re-embedded on every rerender). If it isn't an available variant,
	      snap the sibling radios to the nearest one that is — the user's
	      click always wins, then keep phone model, then case type, then
	      colour; ties fall back to display order.
	   2. Hand the final option-value ids to the theme's public
	      selectCombination(), which does the fetch, partial rerender, URL
	      update and variant:change event exactly as vanilla would. */
	/* Linked case products (plain cases are one product per case type and
	   colour): an option this product does not have is a radio carrying
	   data-vpc-link, the sibling's URL for the same phone. Choosing it opens
	   that product; nothing re-renders here. */
	document.addEventListener('change', (event) => {
		const input = event.target;
		if (!input.matches || !input.matches('input[data-vpc-link]')) return;
		const option = input.getAttribute('data-vpc-link-option');
		const value = input.getAttribute('data-vpc-link-value');
		if (option === 'phone_model') rememberPhoneModel(value);
		if (typeof window.bsTrack === 'function') {
			window.bsTrack('linked_product_switch', { option, to: value, from_path: location.pathname });
		}
		window.location.href = input.getAttribute('data-vpc-link');
	});

	// A Back return from the sibling must not show the tapped option as chosen.
	window.addEventListener('pageshow', (event) => {
		if (!event.persisted) return;
		document.querySelectorAll('variant-picker.variant-picker-cases input[type="radio"]').forEach((radio) => {
			radio.checked = radio.defaultChecked;
		});
	});

	document.addEventListener('change', (event) => {
		const input = event.target;
		if (!input.matches || !input.matches('input[data-vpc-position]')) return;
		const formId = input.getAttribute('form');
		const picker = document.querySelector(`variant-picker.variant-picker-cases[form-id="${formId}"]`);
		if (!picker || typeof picker.selectCombination !== 'function') return;
		const form = document.forms[formId];
		const matrixEl = picker.querySelector('[data-vpc-matrix]');
		if (!form || !matrixEl) return;

		let matrix;
		try {
			matrix = JSON.parse(matrixEl.textContent);
		} catch (e) {
			return;
		}
		const { values, variants } = matrix;
		const changedOption = values[input.value];
		const changedPos = changedOption && changedOption[0];
		const changedName = changedOption && changedOption[1];
		if (changedPos === 1) rememberPhoneModel(changedName);

		const radios = Array.from(form.elements).filter((el) => el.matches && el.matches('input[data-vpc-position]'));
		const selected = {};
		radios.forEach((r) => {
			if (!r.checked) return;
			const v = values[r.value];
			if (v) selected[v[0]] = v[1];
		});

		/* Matrix state per variant: 1 = in stock, 2 = pre-order (continue-selling
		   with nothing on hand — see snippets/backorder-window.liquid), 0 = not
		   sellable. Pre-orders are real, slower sales, so the rule is:
		     - an explicit tap on a case card or colour swatch is honoured even
		       when that combination is a pre-order (the shopper chose to wait);
		     - a phone-model change, or a combination that does not exist,
		       prefers the nearest IN-STOCK sibling and only falls back to a
		       pre-order when nothing for that phone is on the shelf.
		   That is the "sell what we can ship today first" steer, measured as
		   `backorder_steer` in PostHog. */
		const matches = (v) => [1, 2, 3].every((p) => !selected[p] || v[p - 1] === selected[p]);
		const WEIGHT = { 1: 4, 2: 2, 3: 1 };
		const best = (list) => {
			let winner = list[0];
			let bestScore = -1;
			list.forEach((v) => {
				let score = 0;
				[1, 2, 3].forEach((p) => {
					if (p !== changedPos && selected[p] && v[p - 1] === selected[p]) score += WEIGHT[p];
				});
				if (score > bestScore) {
					bestScore = score;
					winner = v;
				}
			});
			return winner;
		};
		const forChanged = (state) =>
			changedName
				? variants.filter((v) => (state ? v[3] === state : Boolean(v[3])) && v[changedPos - 1] === changedName)
				: [];

		const exactInStock = variants.find((v) => v[3] === 1 && matches(v));
		const exactBackorder = variants.find((v) => v[3] === 2 && matches(v));
		let target = exactInStock || null;
		let steered = false;

		if (!target) {
			// A colour tap is exact intent: honour it even as a pre-order (the
			// swatch is tagged). A case-card tap is about the case, so land on
			// an in-stock colour of it for the SAME phone when one exists — the
			// card only shows "Pre-order" when no colour is on the shelf.
			// A phone-model change may move case type and colour to stock.
			let inStock = forChanged(1);
			if (changedPos !== 1) inStock = inStock.filter((v) => v[0] === selected[1]);
			if (exactBackorder && changedPos === 3) {
				target = exactBackorder;
			} else if (inStock.length) {
				target = best(inStock);
				steered = Boolean(exactBackorder);
			} else if (exactBackorder) {
				target = exactBackorder;
			} else {
				const any = forChanged(0);
				if (any.length) target = best(any);
			}
			// No sellable candidate at all: fall through with the raw
			// selection so the theme renders its unavailable state.
		}

		if (target) {
			[1, 2, 3].forEach((p) => {
				if (p === changedPos || !target[p - 1] || target[p - 1] === selected[p]) return;
				const radio = radios.find((r) => {
					const v = values[r.value];
					return v && v[0] === p && v[1] === target[p - 1];
				});
				if (radio) radio.checked = true;
			});
			if (steered) {
				window.bsTrack?.('backorder_steer', {
					reason: changedPos === 1 ? 'model_change' : 'case_tap',
					model: target[0],
					from_case: selected[2] || null,
					from_colour: selected[3] || null,
					to_case: target[1],
					to_colour: target[2],
				});
			}
		}

		const optionValues = radios
			.filter((r) => r.checked)
			.sort((a, b) => parseInt(a.getAttribute('data-vpc-position'), 10) - parseInt(b.getAttribute('data-vpc-position'), 10))
			.map((r) => r.value);

		picker.selectCombination({ optionValues, productChange: false });
	});

	/* --- 1. Brand toggle (delegated, survives rerenders) --- */
	document.addEventListener('click', (event) => {
		const btn = event.target.closest('[data-brand-filter]');
		if (!btn) return;
		const brand = btn.getAttribute('data-brand-filter');
		const popover = btn.closest('x-popover') || document;
		popover.querySelectorAll('[data-brand-filter]').forEach((b) => {
			b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
		});
		const list = popover.querySelector('.vpc-model__list');
		if (list) list.setAttribute('data-active-brand', brand);
	});

	/* Custom popover element: on mobile, slide the sheet up with the same
	   easing as the pill nav menu instead of the theme's clip-path wipe.
	   Desktop keeps the vanilla anchored behavior. */
	customElements.whenDefined('x-popover').then(() => {
		const Popover = customElements.get('x-popover');
		const EASE = 'cubic-bezier(0.4, 0, 0.22, 1)';
		const isMobile = () => window.matchMedia('screen and (max-width: 999px)').matches;
		const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		class VpcPopover extends Popover {
			get _sheetContent() {
				return this.shadowRoot.querySelector('[part="content"]');
			}

			_showTransition(animate = true) {
				if (!isMobile()) return super._showTransition(animate);
				this.style.display = 'block';
				this.style.insetInlineStart = '0px';
				this.style.insetInlineEnd = null;
				this.style.insetBlockEnd = '0px';
				this.style.insetBlockStart = null;
				this.style.visibility = 'visible';
				this.style.opacity = '1';
				const content = this._sheetContent;
				content.getAnimations().forEach((a) => a.cancel());
				const anim = content.animate(
					[{ transform: 'translateY(110%)' }, { transform: 'translateY(0)' }],
					{ duration: 400, easing: EASE, fill: 'both' }
				);
				if (!animate || reduced()) anim.finish();
				// A rapid re-toggle cancels this animation and REJECTS .finished;
				// unsettled, that swallows dialog:after-show upstream (frozen focus
				// trap) — always settle. See the drawer contract in AGENTS.md.
				return anim.finished.catch(() => {});
			}

			_hideTransition() {
				if (!isMobile()) return super._hideTransition();
				const content = this._sheetContent;
				content.getAnimations().forEach((a) => a.cancel());
				const anim = content.animate(
					[{ transform: 'translateY(0)' }, { transform: 'translateY(110%)' }],
					{ duration: 300, easing: EASE, fill: 'both' }
				);
				if (reduced()) anim.finish();
				return anim.finished.catch(() => {}).then(() => {
					this.style.opacity = '0';
					this.style.visibility = 'hidden';
					this.style.display = 'none';
				});
			}
		}

		if (!customElements.get('vpc-popover')) {
			customElements.define('vpc-popover', VpcPopover);
		}
	});

	/* Case-info drawer: same slide-up as the model sheet on mobile; on desktop
	   (where we restyle it as a centered modal) a soft fade + scale. */
	customElements.whenDefined('x-drawer').then(() => {
		const Drawer = customElements.get('x-drawer');
		const EASE = 'cubic-bezier(0.4, 0, 0.22, 1)';
		const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		class VpcDrawer extends Drawer {
			get _surface() {
				return this.shadowRoot.querySelector('[part="content"]');
			}

			_animateSurface(keyframes, duration) {
				this.style.display = 'block';
				this.style.visibility = 'visible';
				this.style.opacity = '1';
				const surface = this._surface;
				surface.getAnimations().forEach((a) => a.cancel());
				const anim = surface.animate(keyframes, { duration, easing: EASE, fill: 'both' });
				if (reduced()) anim.finish();
				return anim;
			}

			_showTransition(animate = true) {
				const mobile = window.matchMedia('screen and (max-width: 699px)').matches;
				/* Anchor the host before showing: the theme's .drawer CSS sets no
				   block insets, so an unanchored fixed host lands at its in-flow
				   page position (grey overlay, sheet off-screen). Mobile pins it
				   to the bottom as a sheet (same as vpc-popover); desktop clears
				   the inline styles so the CSS modal rules take over. */
				if (mobile) {
					this.style.insetBlockEnd = '0px';
					this.style.insetBlockStart = null;
					this.style.insetInlineStart = '0px';
					this.style.insetInlineEnd = '0px';
				} else {
					this.style.insetBlockEnd = null;
					this.style.insetBlockStart = null;
					this.style.insetInlineStart = null;
					this.style.insetInlineEnd = null;
				}
				const anim = this._animateSurface(
					mobile
						? [{ transform: 'translateY(110%)' }, { transform: 'translateY(0)' }]
						: [{ opacity: 0, transform: 'scale(0.96)' }, { opacity: 1, transform: 'scale(1)' }],
					mobile ? 400 : 250
				);
				if (!animate) anim.finish();
				// Settle on cancellation — same contract as vpc-popover above.
				return anim.finished.catch(() => {});
			}

			_hideTransition() {
				const mobile = window.matchMedia('screen and (max-width: 699px)').matches;
				const anim = this._animateSurface(
					mobile
						? [{ transform: 'translateY(0)' }, { transform: 'translateY(110%)' }]
						: [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.96)' }],
					mobile ? 300 : 200
				);
				return anim.finished.catch(() => {}).then(() => {
					this.style.opacity = '0';
					this.style.visibility = 'hidden';
					this.style.display = 'none';
				});
			}
		}

		if (!customElements.get('vpc-drawer')) {
			customElements.define('vpc-drawer', VpcDrawer);
		}
	});

	/* Opening the model picker: reveal the current selection (long list on
	   mobile would otherwise always start at the top). */
	document.addEventListener('click', (event) => {
		const trigger = event.target.closest('.vpc-model__button');
		if (!trigger) return;
		const popover = document.getElementById(trigger.getAttribute('aria-controls'));
		if (!popover) return;
		setTimeout(() => {
			const row = popover.querySelector('.vpc-model__row:has(input:checked)') || popover.querySelector('.vpc-model__row input:checked')?.closest('.vpc-model__row');
			// Scroll ONLY the popover's own scroll container ([part="body"]).
			// scrollIntoView also scrolled the window when the row sat low in
			// the list, so reopening the picker nudged the page down.
			const body = popover.shadowRoot?.querySelector('[part="body"]');
			if (!row || !body) return;
			const delta = row.getBoundingClientRect().top - body.getBoundingClientRect().top - (body.clientHeight - row.offsetHeight) / 2;
			body.scrollTop += delta;
		}, 120);
	});

	/* --- Shared helpers --- */

	let watchedGallery = null;
	let watchedInfo = null;
	let currentVariant = null;
	let caseSpecs = {};

	const formatPrice = (cents) => {
		try {
			return new Intl.NumberFormat(document.documentElement.lang || 'en', {
				style: 'currency',
				currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP',
			}).format(cents / 100);
		} catch (e) {
			return '';
		}
	};

	const variantLabel = (variant) =>
		(variant && (variant.options || []).filter(Boolean).join(' · ')) || '';

	const updateGalleryFeatureBadges = (variant) => {
		const badges = watchedGallery?.querySelector('[data-vpc-gallery-feature-badges]');
		if (!badges || !variant) return;
		const caseType = (variant.options || []).find((option) => caseSpecs[String(option).toLowerCase()]);
		const specs = caseType && caseSpecs[String(caseType).toLowerCase()];
		if (!caseType || !specs) {
			badges.replaceChildren();
			return;
		}

		const featureParts = [];
		// Offer badge leads the stack (2026-09-09): awareness on the IMAGE, in the
		// first half-second, before the price registers. Says the offer, not the
		// maths — the module above the buy button does the maths. Case PDPs only
		// (the offer module's presence is the qualifying signal), and it opens the
		// same how-it-works sheet.
		if (document.querySelector('.bs-bogof-flash')) {
			const offer = document.createElement('button');
			offer.type = 'button';
			offer.className = 'vpc-gallery-feature-badge vpc-gallery-feature-badge--offer';
			offer.textContent = '2nd case FREE';
			offer.setAttribute('aria-label', 'Buy 1 Get 1 Free — how it works');
			if (document.getElementById('vpc-bogof-info')) offer.setAttribute('aria-controls', 'vpc-bogof-info');
			featureParts.push(offer);
		}
		if (specs.drop) {
			const drop = document.createElement('span');
			drop.className = 'vpc-gallery-feature-badge';
			drop.textContent = `${specs.drop} drop`;
			featureParts.push(drop);
		}
		if (specs.highlight) {
			const highlightedFeature = document.createElement('span');
			highlightedFeature.className = 'vpc-gallery-feature-badge vpc-gallery-feature-badge--highlight';
			highlightedFeature.textContent = specs.highlight;
			if (specs.highlightColor) highlightedFeature.style.backgroundColor = specs.highlightColor;
			featureParts.push(highlightedFeature);
		}
		if (specs.magsafe) {
			const magsafe = document.createElement('span');
			magsafe.className = 'vpc-gallery-feature-badge vpc-gallery-feature-badge--highlight';
			magsafe.textContent = 'MagSafe';
			if (specs.highlightColor) magsafe.style.backgroundColor = specs.highlightColor;
			featureParts.push(magsafe);
		}
		badges.replaceChildren(...featureParts);
	};

	/* Spec gauges above the case cards (snippets/case-spec-gauges.liquid).
	   Bars are rewritten rather than re-rendered so the row never reflows while
	   the shopper is tapping between case types. The host is looked up on every
	   call, never cached: the picker replaces its whole DOM on a variant
	   change, so a reference taken at init points at a detached node. */
	const updateCaseGauges = (variant) => {
		const gaugeHost = document.querySelector('.shopify-section--main-product [data-vpc-gauges]');
		if (!gaugeHost || !variant) return;
		const caseType = (variant.options || []).find((option) => caseSpecs[String(option).toLowerCase()]);
		const specs = caseType && caseSpecs[String(caseType).toLowerCase()];
		if (!specs) return;

		const paint = (name, steps, readout) => {
			const gauge = gaugeHost.querySelector(`[data-vpc-gauge="${name}"]`);
			if (!gauge) return;
			const track = gauge.querySelector('[data-vpc-gauge-track]');
			const max = Number(track?.dataset.vpcGaugeMax) || 6;
			const filled = Math.max(0, Math.min(max, Number(steps) || 0));
			track?.querySelectorAll('.vpc-gauge__step').forEach((step, index) => {
				step.classList.toggle('is-filled', index < filled);
			});
			if (track) {
				track.setAttribute(
					'aria-label',
					`${name === 'impact' ? 'Impact level' : 'Thickness'} ${filled} out of ${max}`
				);
			}
			const readoutEl = gauge.querySelector('[data-vpc-gauge-readout]');
			if (readoutEl) readoutEl.textContent = readout || '';
		};

		const drop = [specs.drop, specs.dropMetric].filter(Boolean).join(' | ');
		paint('impact', specs.impactSteps, drop);
		paint('thickness', specs.thicknessSteps, '');
	};

	const galleryFor = (form) => {
		const section = form.closest('.shopify-section') || document;
		return section.querySelector('.product-gallery');
	};

	const initialVariantFor = (picker) => {
		try {
			return JSON.parse(picker.querySelector('script[data-variant]').textContent);
		} catch (e) {
			return null;
		}
	};

	/* --- 2. Header pill info row: the pill grows to show the live selection ---
	   When the gallery is scrolled away on mobile, the header pill expands
	   downward (same mechanic as the nav menu) with the current variant's
	   photo, selection, price + specs, and a scroll-to-top button. */

	let infoRow = null;

	const ensureInfoRow = () => {
		if (infoRow) return infoRow;
		const wrapper = document.querySelector('.header-pill .header__wrapper');
		if (!wrapper) return null;
		infoRow = document.createElement('div');
		infoRow.className = 'vpc-header-info';
		infoRow.innerHTML =
			'<div class="vpc-header-info__inner">' +
			'<img class="vpc-header-info__image" alt="" src="">' +
			'<span class="vpc-header-info__text">' +
			'<span class="vpc-header-info__title"></span>' +
			'<span class="vpc-header-info__meta"></span>' +
			'</span>' +
			'<button type="button" class="vpc-header-info__top" aria-label="Back to product images">' +
			'<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 7.5 6 3.5l4 4"/></svg>' +
			'</button>' +
			'</div>';
		infoRow.addEventListener('click', () => {
			watchedGallery?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		});
		wrapper.appendChild(infoRow);
		return infoRow;
	};

	const updateInfoRow = (variant) => {
		const row = ensureInfoRow();
		if (!row || !variant) return;
		const media = variant.featured_media && variant.featured_media.preview_image;
		if (media && media.src) {
			const joiner = media.src.includes('?') ? '&' : '?';
			row.querySelector('.vpc-header-info__image').src = media.src + joiner + 'width=96';
		}
		row.querySelector('.vpc-header-info__title').textContent = variantLabel(variant);
		const specs = (variant.options || []).reduce(
			(found, opt) => found || caseSpecs[String(opt).toLowerCase()],
			null
		);
		const meta = [formatPrice(variant.price)];
		if (specs && specs.drop) meta.push(specs.drop + ' drop');
		if (specs) meta.push(specs.magsafe ? 'MagSafe' : 'No MagSafe');
		row.querySelector('.vpc-header-info__meta').textContent = meta.filter(Boolean).join(' \u00b7 ');
	};

	/* The row expands the sticky header. If the pill's resting footprint isn't
	   locked (captureRestingHeaderHeight only runs from navigation-box), the
	   header's in-flow height grows with the row, shifts the page, re-crosses
	   the scroll threshold and oscillates. Lock it here too before expanding. */
	const lockRestingHeaderHeight = () => {
		const section = document.querySelector('.shopify-section--header-pill');
		if (!section || section.style.getPropertyValue('--bs-header-resting-height')) return;
		const storeHeader = section.querySelector('store-header');
		if (!storeHeader || storeHeader.hasAttribute('expanded')) return;
		// Measure the header content, not the section — below 1150px the section's
		// height is this very lock's fallback chain (see header-pill.js).
		const height = Math.round(storeHeader.getBoundingClientRect().height);
		if (height >= 60) section.style.setProperty('--bs-header-resting-height', `${height}px`);
	};

	let infoRowShown = false;

	const checkInfoRow = () => {
		if (!watchedGallery || !watchedInfo) return;
		const row = ensureInfoRow();
		if (!row) return;
		if (!window.matchMedia('(max-width: 699px)').matches) {
			infoRowShown = false;
			row.classList.remove('is-visible');
			return;
		}
		/* Hysteresis: the show/hide band is wider than the row's own height, so
		   even when toggling the row shifts the page (resting lock missing), the
		   shift can't re-cross the opposite threshold and jiggle the page. The
		   row's content is only rebuilt on the hidden→shown edge, not per frame. */
		const bottom = watchedGallery.getBoundingClientRect().bottom;
		const show = infoRowShown ? bottom < 190 : bottom < 100;
		if (show === infoRowShown) return;
		infoRowShown = show;
		if (show) {
			lockRestingHeaderHeight();
			updateInfoRow(currentVariant);
		}
		row.classList.toggle('is-visible', show);
	};

	let ticking = false;
	window.addEventListener(
		'scroll',
		() => {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(() => {
				ticking = false;
				checkInfoRow();
			});
		},
		{ passive: true }
	);
	// iOS URL-bar collapse and rotation move the threshold without a scroll event.
	window.addEventListener('resize', () => checkInfoRow(), { passive: true });

	/* --- 3. Above-the-fold offer: gallery chip + one-time pill announce --- */

	const ensureOfferChip = (gallery) => {
		const host = gallery.querySelector('.product-gallery__media-list-wrapper') || gallery;
		if (host.querySelector('.vpc-gallery-badge-row')) return;
		const row = document.createElement('div');
		row.className = 'vpc-gallery-badge-row';
		// The BOGOF price flash under the price owns the offer message on case
		// PDPs now — a second BUY 1 GET 1 FREE chip in the same viewport read
		// as offer noise (user feedback 2026-08-26). Pages without the flash
		// (e.g. Mystery bundles fall back to vanilla pickers anyway) keep it.
		if (!document.querySelector('.bs-bogof-flash')) {
			const chip = document.createElement('button');
			chip.type = 'button';
			chip.className = 'vpc-offer-chip';
			chip.textContent = 'BUY 1 GET 1 FREE';
			chip.setAttribute('aria-label', 'BUY 1 GET 1 FREE — how it works');
			if (document.getElementById('vpc-bogof-info')) {
				// The how-it-works sheet wires up any [aria-controls] pointing at it
				chip.setAttribute('aria-controls', 'vpc-bogof-info');
				chip.setAttribute('aria-expanded', 'false');
			} else {
				chip.addEventListener('click', () => {
					document.querySelector('.bogof-meter, .bogof-meter--product')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
				});
			}
			row.append(chip);
		}
		const featureBadges = document.createElement('div');
		featureBadges.className = 'vpc-gallery-feature-badges';
		featureBadges.dataset.vpcGalleryFeatureBadges = '';
		row.append(featureBadges);
		host.appendChild(row);
	};

	/* --- Wire up on load + on variant changes --- */

	const init = () => {
		const picker = document.querySelector('.shopify-section--main-product .variant-picker-cases');
		if (!picker) return;
		const form = document.forms[picker.getAttribute('form-id')];
		if (!form) return;
		watchedGallery = galleryFor(form);
		watchedInfo =
			form.closest('.product-info') ||
			(form.closest('.shopify-section') || document).querySelector('.product-info');
		currentVariant = initialVariantFor(picker);
		try {
			caseSpecs = JSON.parse(picker.querySelector('[data-vpc-case-specs]').textContent);
		} catch (e) {
			caseSpecs = {};
		}
		if (watchedGallery) ensureOfferChip(watchedGallery);
		updateGalleryFeatureBadges(currentVariant);
		updateCaseGauges(currentVariant);
	};

	/* RRP pill in the page head (snippets/product-rrp-pill.liquid). The head is
	   outside the vendor's re-rendered blocks, so repaint it from the freshly
	   swapped <buy-buttons> attributes (Liquid-formatted money, so the figure
	   matches the button's price format exactly). No anchor → hidden. */
	const updateRrpPill = () => {
		const pill = document.querySelector('[data-bs-rrp-pill]');
		if (!pill) return;
		const buyButtons = document.querySelector('.product-info__buy-buttons buy-buttons[data-bs-stock]');
		const rrp = buyButtons ? buyButtons.dataset.bsRrp || '' : '';
		const save = buyButtons ? buyButtons.dataset.bsSave || '' : '';
		if (!rrp || !save) {
			pill.hidden = true;
			return;
		}
		const saveEl = pill.querySelector('[data-bs-rrp-save]');
		const priceEl = pill.querySelector('[data-bs-rrp-price]');
		if (saveEl) saveEl.textContent = (pill.dataset.saveTemplate || '[percent]% off').replace('[percent]', save);
		if (priceEl) priceEl.textContent = rrp;
		pill.hidden = false;
	};

	document.addEventListener('variant:change', (event) => {
		const variant = event.detail && event.detail.variant;
		if (!variant) return;
		currentVariant = variant;
		updateGalleryFeatureBadges(variant);
		updateCaseGauges(variant);
		updateRrpPill();
		if (infoRowShown) updateInfoRow(variant);
		checkInfoRow();
	});

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	/* The newsletter popup's model pick must repoint the PDP picker in place
	   (no page reload happens anywhere any more). Checking the matching model
	   radio and dispatching change reuses the closest-match snap + single
	   selectCombination() rerender above; the resulting rememberPhoneModel()
	   call is a no-op because the popup pick already locked the preference. */
	window.addEventListener('blancspace:phone-model-change', (event) => {
		if (event.detail?.origin !== 'popup' || !event.detail?.model) return;
		document.querySelectorAll('variant-picker.variant-picker-cases').forEach((picker) => {
			const form = document.forms[picker.getAttribute('form-id')];
			const matrixEl = picker.querySelector('[data-vpc-matrix]');
			if (!form || !matrixEl) return;
			let matrix;
			try {
				matrix = JSON.parse(matrixEl.textContent);
			} catch (e) {
				return;
			}
			const entry = Object.entries(matrix.values).find(([, v]) => v[0] === 1 && v[1] === event.detail.model);
			if (!entry) return; // this product has no variant for the picked model
			const radio = Array.from(form.elements).find(
				(el) => el.matches && el.matches('input[data-vpc-position]') && el.value === entry[0]
			);
			if (!radio || radio.checked) return;
			radio.checked = true;
			radio.dispatchEvent(new Event('change', { bubbles: true }));
		});
	});
})();
